import { useEffect, useState } from "react";
import { Button, Modal, Table, Popconfirm, Space } from "antd";
import ApiAuth from "../../../api/auth";
import useNotification from "../../notification/notification";
import { registerPasskey } from "../../../common/utils/passkeys/passkeys";

export default function PasskeysModal({ open, onClose, t }) {
  const { openNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [items, setItems] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await ApiAuth.passkeyList();
      setItems(res.data?.passkeys || res.data?.credentials || []);
    } catch (e) {
      openNotification({ type: "error", message: <p>{t("profile.passkey.list_failed")}</p> });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const onAdd = async () => {
    setAdding(true);
    try {
      await registerPasskey();
      openNotification({ type: "success", message: <p>{t("login.passkey.registered")}</p> });
      await load();
    } catch (e) {
      if (e?.name === "NotAllowedError") return;
      openNotification({ type: "error", message: <p>{t("login.passkey.register_failed")}</p> });
    } finally {
      setAdding(false);
    }
  };

  const onDelete = async (id) => {
    try {
      await ApiAuth.passkeyDelete(id);
      openNotification({ type: "success", message: <p>{t("profile.passkey.deleted")}</p> });
      await load();
    } catch (e) {
      openNotification({ type: "error", message: <p>{t("profile.passkey.delete_failed")}</p> });
    }
  };

  const columns = [
    {
      title: t("profile.passkey.created_at"),
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v) => (v ? new Date(v).toLocaleString() : "—"),
    },
    {
      title: t("profile.passkey.last_used_at"),
      dataIndex: "lastUsedAt",
      key: "lastUsedAt",
      render: (v) => (v ? new Date(v).toLocaleString() : "—"),
    },
    {
      title: t("modals.actions"),
      key: "actions",
      align: "right",
      render: (_, row) => (
        <Space>
          <Popconfirm
            title={t("profile.passkey.confirm_delete")}
            okText={t("modals.delete")}
            cancelText={t("modals.cancel")}
            onConfirm={() => onDelete(row.id)}
          >
            <Button danger>{t("modals.delete")}</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Modal
      title={t("profile.passkey.manage_title")}
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          {t("modals.close")}
        </Button>,
        <Button key="add" type="primary" loading={adding} onClick={onAdd}>
          {t("profile.passkey.add_new")}
        </Button>,
      ]}
      width={720}
    >
      <Table
        rowKey="id"
        loading={loading}
        dataSource={items}
        columns={columns}
        pagination={false}
      />
    </Modal>
  );
}